/**
 * Talking to the server.
 *
 * Every request the app makes goes through here, so there is one place that
 * knows how an error is reported and one place to look when something isn't
 * loading.
 */
import type { ExtraKey } from "../../server/nutrients";

export type Entry = {
  id: number;
  day: string;
  logged_at: string;
  kind: "food" | "drink" | "exercise";
  name: string;
  servings: number;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  fiber_g: number | null;
  sugar_g: number | null;
  sodium_mg: number | null;
  minutes: number | null;
  barcode: string | null;
  source: string;
  notes: string | null;
} & Record<ExtraKey, number | null>;

export type Totals = {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  sugar_g: number;
  sodium_mg: number;
} & Record<ExtraKey, number>;

export type Settings = {
  creature_name: string;
  goal_calories: number;
  goal_protein_g: number;
  goal_carbs_g: number;
  goal_fat_g: number;
};

export type Day = {
  date: string;
  entries: Entry[];
  totals: Totals;
  burned: number;
  movedMinutes: number;
  net: number;
  settings: Settings;
};

export type Product = {
  barcode: string;
  name: string;
  brand: string | null;
  quantity: string | null;
  serving_size: string | null;
  calories_100: number | null;
  protein_100: number | null;
  carbs_100: number | null;
  fat_100: number | null;
  fiber_100: number | null;
  sugar_100: number | null;
  sodium_100: number | null;
  serving_g: number | null;
  calories_serving: number | null;
  image_url: string | null;
} & Record<`${ExtraKey}_100`, number | null>;

export type LabelReading = {
  readable: boolean;
  note: string | null;
  product_name: string | null;
  serving_size: string | null;
  servings_per_container: number | null;
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  fiber_g: number | null;
  sugar_g: number | null;
  sodium_mg: number | null;
  /** Only for labels that list salt instead of sodium. */
  salt_g: number | null;
} & Record<ExtraKey, number | null>;

/** Who is signed in, as the server sees it. */
export type Me = {
  signedIn: boolean;
  email: string | null;
  /** True for the pretend local user that `npm run dev` signs you in as. */
  dev: boolean;
  /** False when signing in cannot work because the server is missing settings. */
  configured: boolean;
};

/**
 * An error the app can show to Sam as-is.
 *
 * `kind` separates the ones worth handling differently: a missing barcode is
 * an invitation to photograph the label, an unavailable scanner is a feature
 * being off rather than anything being broken, and being signed out is not an
 * error at all -- it just means the app should show the sign-in screen.
 */
export class ApiError extends Error {
  status: number;
  kind: "not-found" | "unavailable" | "signed-out" | "other";
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.kind =
      status === 404
        ? "not-found"
        : status === 503
          ? "unavailable"
          : status === 401
            ? "signed-out"
            : "other";
  }
}

/**
 * Announced once whenever the server says we are no longer signed in, so the
 * app can show the sign-in screen from wherever Sam happens to be.
 *
 * A single announcement here beats every screen checking for itself: a session
 * can run out at any moment, in the middle of any request, and this way no
 * screen has to remember to cope with it.
 */
export const SIGNED_OUT_EVENT = "sft:signed-out";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    });
  } catch {
    // fetch only rejects when the request never happened at all -- no signal,
    // or the server is not running.
    throw new ApiError("Can't reach the server. Are you online?", 0);
  }

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) {
      // Tell the app, so it can show the sign-in screen rather than an error
      // message about something Sam cannot do anything about.
      window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
    }
    const message =
      (body && typeof body.error === "string" && body.error) || `Something went wrong (${response.status}).`;
    throw new ApiError(message, response.status);
  }
  return body as T;
}

export const api = {
  me: () => request<Me>("/auth/me"),

  day: (date: string) => request<Day>(`/day/${date}`),

  addEntry: (entry: Record<string, unknown>) =>
    request<Entry>("/entries", { method: "POST", body: JSON.stringify(entry) }),

  deleteEntry: (id: number) => request<void>(`/entries/${id}`, { method: "DELETE" }),

  recent: (search = "") =>
    request<
      (Pick<Entry, "name" | "kind" | "barcode" | "calories" | "protein_g" | "carbs_g" | "fat_g" |
        "fiber_g" | "sugar_g" | "sodium_mg" | ExtraKey | "servings" | "minutes"> & { times: number })[]
    >(`/recent?q=${encodeURIComponent(search)}`),

  product: (barcode: string) => request<Product>(`/product/${encodeURIComponent(barcode)}`),

  labelAvailable: () => request<{ available: boolean }>("/label/available"),

  readLabel: (imageBase64: string, mimeType: string) =>
    request<LabelReading>("/label", {
      method: "POST",
      body: JSON.stringify({ imageBase64, mimeType }),
    }),

  settings: () => request<Settings>("/settings"),

  saveSettings: (settings: Partial<Settings>) =>
    request<Settings>("/settings", { method: "PUT", body: JSON.stringify(settings) }),
};
